import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { UPSERT_ASSET_FORM_ID } from '../../components/UpsertAssetModal/UpsertAssetModal.meta';
import { RealEstateDTO } from '../../tables/RealEstate/RealEstate.meta';
import { useRealEstateForm } from './useRealEstateForm';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Label } from '../../../../CommonComponents/OPL/Label/Label';
import { Radio } from '../../../../CommonComponents/OPL/Radio/Radio';
import { HelperText } from '../../../../CommonComponents/OPL/HelperText/HelperText';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { SearchInstitutionsSelect } from '../../components/SearchInstitutionsSelect/SearchInstitutionsSelect';
import { InputMask } from '../../../../CommonComponents/OPL/Input/InputMask';
import { Checkbox } from '../../../../CommonComponents/OPL/Checkbox/Checkbox';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import { AssetBucketSelect } from '../components/AssetBucketSelect/AssetBucketSelect';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { GrossEstateCheckbox } from '../components/GrossEstateCheckbox/GrossEstateCheckbox';
import { getIsOwnerTrust } from '../utils/getIsOwnerTrust';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { RealEstateFormValues } from './RealEstateForm.meta';
import { AssetOwnership } from '../components/AssetOwnership/AssetOwnership';
import { APPEND_ASSET_OWNER, OwnerModelFormDTO } from '../../OtherAssets.meta';

export interface Props {
  clientId: string;
  asset: RealEstateDTO | null;
  options: OtherAssetsDTO['Options'];
  onSuccess: () => void;
  isEstatePlanningWorkflow: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

export const RealEstateForm = ({
  clientId,
  options,
  asset,
  onSuccess,
  isEstatePlanningWorkflow,
  onDirtyChange,
}: Props) => {
  const {
    data: {
      formBag,
      mutationBag,
      ownerOptions,
      mortgageTypeOptions,
      saleYearOptions,
      showMortgageSelect,
      showMortgageFields,
      showMonthlyPayment,
      mortgageOptions,
      mortgageInstitution,
      defaultShowAdvanced,
      assetBucketOptions,
      assetBucketingByManagementStyle,
      managementStylesOptions,
      onManagementSelect,
      allowAssetBucketingCustomization,
      displayAssetBucketing,
    },
    actions: { onSubmit, setMortgageInstitution, linkExistingMortgage, resetValues },
  } = useRealEstateForm({ clientId, options, asset, onSuccess });

  const {
    register,
    control,
    formState: { errors, isDirty },
    setValue,
    watch,
  } = formBag;

  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);

  const [ownerModel, managementStyleSelection] = watch(['ownerModel', 'orionManagementStyleId']);

  const isOwnerTrust =
    ownerModel.length === 1 ? getIsOwnerTrust(ownerModel[0].Name ?? '', ownerOptions) : false;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    resetValues();
  }, [asset]);

  const { fields, update, append, remove } = useFieldArray<
    RealEstateFormValues,
    'ownerModel',
    'fieldKey'
  >({
    control,
    name: 'ownerModel',
    keyName: 'fieldKey',
  });

  const watchOwner = useWatch({ control, name: 'ownerModel' });

  const ownerModelFields = fields.map((field, index) => ({
    ...field,
    ...watchOwner[index],
  }));

  const updateOwner = (index: number, data: OwnerModelFormDTO) => {
    update(index, {
      ...ownerModelFields[index],
      ...data,
    });
  };

  const setPercentage = (percentArr: number[]) => {
    ownerModelFields.map((field, index) => {
      formBag.setValue(`ownerModel.${index}.Percentage`, percentArr[index].toString());
    });
  };

  return (
    <form
      onSubmit={onSubmit}
      id={UPSERT_ASSET_FORM_ID}
      css={`
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.space['16']}px;
      `}
      aria-label="Real estate form"
    >
      {mutationBag.error && (
        <Alert
          variant="error"
          message={mutationBag.error.message}
          onRequestClose={mutationBag.reset}
        />
      )}

      <Input
        label="Property Name"
        formVariant={errors.name && 'error'}
        helperText={errors.name?.message}
        maxLength={32}
        {...register('name')}
      />

      <AssetOwnership<RealEstateFormValues>
        control={control}
        options={options.RealEstateOptions.ownerOptions}
        allowsUnequalOwnership={options.RealEstateOptions.AllowUnequalOwnership}
        ownerModelFields={ownerModelFields}
        updateOwner={updateOwner}
        appendOwner={() => append({ ...APPEND_ASSET_OWNER })}
        removeOwner={(index: number) => remove(index)}
        setPercentage={setPercentage}
        error={errors?.ownerModel ? errors?.ownerModel[0]?.Id?.message : ''}
      />

      <Box
        css={`
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${({ theme }) => theme.space['16']}px;
        `}
      >
        <Controller
          control={control}
          name="marketValue"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Market Value"
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              formVariant={errors.marketValue && 'error'}
              helperText={errors.marketValue?.message}
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);
              }}
              {...field}
            />
          )}
        />

        <Controller
          name="mortgageType"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              label="Mortgage Type"
              value={mortgageTypeOptions.find(option => option.value === value)}
              options={mortgageTypeOptions}
              onChange={option => {
                onChange(option?.value);

                if (option?.value === 'Existing') {
                  setValue('mortgageId', null);
                }
              }}
              isPortal
            />
          )}
        />
      </Box>

      {showMortgageSelect && (
        <Controller
          name="mortgageId"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              label="Mortgage"
              value={mortgageOptions.find(option => option.value === value)}
              options={mortgageOptions}
              onChange={option => {
                onChange(option?.value);
                linkExistingMortgage(option?.value);
              }}
              placeholder="Select an existing mortgage"
              isPortal
            />
          )}
        />
      )}

      {showMortgageFields && (
        <SearchInstitutionsSelect
          clientId={clientId}
          label="Financial Institution"
          defaultValue={mortgageInstitution}
          onChange={option => setMortgageInstitution(option)}
          placeholder="Search Financial Institution"
          openMenuOnClick={false}
          isClearable
        />
      )}

      {showMortgageFields && (
        <Controller
          control={control}
          name="mortgageBalance"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Outstanding Balance"
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              formVariant={errors.mortgageBalance && 'error'}
              helperText={errors.mortgageBalance?.message}
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);
              }}
              {...field}
            />
          )}
        />
      )}

      {showMortgageFields && (
        <Controller
          control={control}
          name="mortgageInterestRate"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Interest Rate"
              rightIcon="far fa-percent"
              decimalScale={3}
              allowNegative={false}
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);
              }}
              {...field}
            />
          )}
        />
      )}

      {showMonthlyPayment && (
        <Controller
          control={control}
          name="mortgageMonthlyPayment"
          render={({ field: { onChange, ...field } }) => (
            <InputNumberMask
              label="Monthly Payment"
              decimalScale={2}
              allowNegative={false}
              leftIcon="far fa-dollar-sign"
              innerText="/month"
              tooltip="Monthly payment includes principal and interest. Real estate taxes, condos fees, home owners insurance can be added in the Expenses step."
              onValueChange={({ floatValue }) => {
                onChange(floatValue ?? null);
              }}
              {...field}
            />
          )}
        />
      )}

      {showMortgageFields && (
        <InputMask
          {...register('mortgageOriginationDate')}
          label="Origination Date"
          type="text"
          mask="99/99/9999"
          placeholder="MM/DD/YYYY"
          leftIcon="far fa-calendar-alt"
          formVariant={errors.mortgageOriginationDate && 'error'}
          helperText={errors.mortgageOriginationDate?.message}
        />
      )}

      {showMortgageFields && options.RealEstateOptions.ShowExcludeFromDebtManagementGoal && (
        <Checkbox {...register('mortgageExcludeFromDebtManagementGoal')}>
          Exclude from debt management goal
        </Checkbox>
      )}

      <Controller
        control={control}
        name="rentalIncome"
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            label="Rental Income"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            innerText="/year"
            tooltip="This is your net income from the property."
            onValueChange={({ floatValue }) => {
              onChange(floatValue ?? null);
            }}
            {...field}
          />
        )}
      />

      <Box role="group" aria-labelledby="real-estate-form--usage-field-label">
        <Label id="real-estate-form--usage-field-label" css="display: block" mb="4">
          Usage
        </Label>

        <Controller
          name="investmentType"
          control={control}
          render={({ field: { value, name, onChange } }) => (
            <>
              {options.RealEstateOptions.investmentTypeOptions.map(option => (
                <Radio
                  key={option.value}
                  name={name}
                  checked={value === option.value}
                  onChange={() => onChange(option.value)}
                  mr="4"
                >
                  {option.label}
                </Radio>
              ))}
            </>
          )}
        />

        {errors.investmentType && (
          <HelperText variant="error">{errors.investmentType.message}</HelperText>
        )}
      </Box>

      {isEstatePlanningWorkflow && !isOwnerTrust && <GrossEstateCheckbox control={control} />}

      {assetBucketingByManagementStyle && (
        <ManagementStyle<RealEstateFormValues>
          control={control}
          options={managementStylesOptions}
          formKey="orionManagementStyleId"
          onManagementSelect={onManagementSelect}
        />
      )}

      {displayAssetBucketing && (
        <AssetBucketSelect<RealEstateFormValues>
          control={control}
          formKey="customBucketIndex"
          isDisabled={
            (assetBucketingByManagementStyle && !allowAssetBucketingCustomization) ||
            (assetBucketingByManagementStyle && managementStyleSelection === null)
          }
          options={assetBucketOptions}
        />
      )}

      {isEstatePlanningWorkflow && (
        <Box>
          <Text as="label" variant="formLabel" color="neutral.600">
            Liquidity Status
          </Text>

          <Box display="flex" flexDirection="column" gridGap="8" mb="8">
            <Box>
              <Controller
                name="isLiquid"
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <>
                    <Radio name={name} checked={value} onChange={() => onChange(true)}>
                      Liquid
                    </Radio>
                    <Radio name={name} checked={!value} onChange={() => onChange(false)} mr="4">
                      Illiquid
                    </Radio>
                  </>
                )}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Controller
            control={control}
            name="assetGrowth"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Asset Growth Rate"
                tooltip="The rate at which the asset’s market value will grow."
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => {
                  onChange(floatValue ?? null);
                }}
                {...field}
              />
            )}
          />

          <Box
            css={`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: ${({ theme }) => theme.space['16']}px;
            `}
          >
            <Controller
              control={control}
              name="propertyTax"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Property Taxes"
                  leftIcon="far fa-dollar-sign"
                  innerText="/year"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => {
                    onChange(floatValue ?? null);
                  }}
                  {...field}
                />
              )}
            />

            <Controller
              control={control}
              name="homeInsurance"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Home Insurance"
                  leftIcon="far fa-dollar-sign"
                  innerText="/year"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => {
                    onChange(floatValue ?? null);
                  }}
                  {...field}
                />
              )}
            />
          </Box>

          {showMortgageFields && (
            <Controller
              control={control}
              name="mortgageMonthlyPmi"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="PMI"
                  leftIcon="far fa-dollar-sign"
                  innerText="/month"
                  tooltip="Private mortgage insurance, also called PMI, is a type of mortgage insurance you might be required to pay for if you have a conventional loan. Like other kinds of mortgage insurance, PMI protects the lender if you stop making payments on your loan."
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => {
                    onChange(floatValue ?? null);
                  }}
                  {...field}
                />
              )}
            />
          )}

          <Controller
            control={control}
            name="monthlyHoa"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Monthly HOA"
                leftIcon="far fa-dollar-sign"
                innerText="/month"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => {
                  onChange(floatValue ?? null);
                }}
                {...field}
              />
            )}
          />

          <Box
            css={`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: ${({ theme }) => theme.space['16']}px;
            `}
          >
            <Controller
              name="saleYear"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Select
                  label="Sale Year"
                  value={value ? ({ value, label: value } as any) : null}
                  options={saleYearOptions}
                  onChange={option => onChange(option?.value || null)}
                  tooltip="If applicable, enter the year this real estate asset will be sold."
                  isClearable
                  isPortal
                />
              )}
            />

            <Controller
              control={control}
              name="saleNetEarnings"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Net Proceeds from Sale"
                  leftIcon="far fa-dollar-sign"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => {
                    onChange(floatValue ?? null);
                  }}
                  tooltip="If applicable, enter the total estimated proceeds from the sale of this asset minus taxes and any fees to sell. Note: If you have a mortgage linked to this asset, the mortgage balance will be paid off as part of the sale and will reduce your net proceeds."
                  {...field}
                />
              )}
            />
          </Box>

          <Controller
            name="assetEarmarks"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                isMulti
                label="Asset Earmark"
                placeholder="Select a goal..."
                value={value}
                options={options.RealEstateOptions.EarmarkOptions}
                onChange={earmarks => onChange(earmarks)}
                noOptionsMessage={() => 'There are no earmarks left to add'}
                closeMenuOnSelect={false}
                isClearable
                isPortal
              />
            )}
          />

          <Controller
            control={control}
            name="incomeGrowth"
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                label="Income Growth"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => {
                  onChange(floatValue ?? null);
                }}
                {...field}
              />
            )}
          />
        </>
      )}
    </form>
  );
};
